import React, { useCallback, useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PRIORITY_META } from "@/lib/constants";
import { Plus, Trash2, ArrowUp, ArrowDown, GripVertical, Info } from "lucide-react";
import { toast } from "sonner";

export default function Templates() {
  const [tid, setTid] = useState("architecture");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [syncPriority, setSyncPriority] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const res = await api.get(`/templates/${tid}`);
    setItems(res.data.items.map((i) => ({ item_id: i.item_id, name: i.name, priority: i.priority || "none" })));
    setLoading(false);
  }, [tid]);
  useEffect(() => { load(); }, [load]);

  const update = (idx, name) => setItems((arr) => arr.map((it, i) => (i === idx ? { ...it, name } : it)));
  const updatePriority = (idx, priority) => setItems((arr) => arr.map((it, i) => (i === idx ? { ...it, priority } : it)));
  const remove = (idx) => setItems((arr) => arr.filter((_, i) => i !== idx));
  const add = () => setItems((arr) => [...arr, { name: "", priority: "none", _tmp: `tmp_${Date.now()}_${Math.random().toString(36).slice(2)}` }]);
  const move = (idx, dir) => setItems((arr) => {
    const j = idx + dir;
    if (j < 0 || j >= arr.length) return arr;
    const copy = [...arr];
    [copy[idx], copy[j]] = [copy[j], copy[idx]];
    return copy;
  });

  const save = async () => {
    const clean = items.filter((it) => it.name.trim());
    if (clean.length === 0) { toast.error("Add at least one task"); return; }
    setSaving(true);
    try {
      const res = await api.put(`/templates/${tid}`, {
        items: clean.map((it) => ({ item_id: it.item_id, name: it.name.trim(), priority: it.priority || "none" })),
        sync_priority: syncPriority,
      });
      setItems(res.data.items.map((i) => ({ item_id: i.item_id, name: i.name, priority: i.priority || "none" })));
      toast.success("Template saved — synced to all template projects");
    } catch (e) {
      toast.error(apiErr(e, "Failed to save template"));
    } finally { setSaving(false); }
  };

  return (
    <div className="p-6 lg:p-10 max-w-[820px]">
      <header className="mb-6">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-zinc-500 mb-1">Admin</p>
        <h1 className="font-heading text-4xl font-black tracking-tighter">Task Templates</h1>
        <p className="text-zinc-500 mt-2 text-sm">The default task checklists applied when you create a new project with &ldquo;Apply Template&rdquo;.</p>
      </header>

      <div className="flex gap-2 mb-6">
        {["architecture", "interior"].map((t) => (
          <button key={t} onClick={() => setTid(t)} data-testid={`template-tab-${t}`}
            className={`px-4 h-9 rounded-sm text-sm font-semibold border capitalize transition-colors ${tid === t ? "bg-zinc-900 text-white border-zinc-900" : "bg-white text-zinc-600 border-zinc-200 hover:bg-zinc-50"}`}>
            {t}
          </button>
        ))}
      </div>

      <div className="flex items-start gap-2 bg-blue-50 border border-blue-200 text-blue-800 text-sm p-3 rounded-sm mb-6">
        <Info className="h-4 w-4 mt-0.5 shrink-0" />
        <p>Editing this template also updates every project that was created from it — renamed tasks are renamed, new tasks are added, and removed tasks are deleted across those projects. Assignments and dates on kept tasks stay intact.</p>
      </div>

      {loading ? (
        <div className="h-8 w-8 border-2 border-zinc-900 border-t-transparent rounded-full animate-spin" />
      ) : (
        <div className="bg-white border border-zinc-200">
          {items.map((it, idx) => (
            <div key={it.item_id || it._tmp} className="flex items-center gap-2 px-4 py-2.5 border-b border-zinc-100 last:border-0" data-testid={`template-item-${idx}`}>
              <GripVertical className="h-4 w-4 text-zinc-300" />
              <span className="text-xs text-zinc-400 w-6">{idx + 1}</span>
              <Input value={it.name} onChange={(e) => update(idx, e.target.value)}
                className="flex-1 rounded-sm border-transparent hover:border-zinc-200 focus:border-zinc-900"
                data-testid={`template-input-${idx}`} placeholder="Task name" />
              <Select value={it.priority || "none"} onValueChange={(v) => updatePriority(idx, v)}>
                <SelectTrigger className="w-[120px] h-9 rounded-sm text-xs" data-testid={`template-priority-${idx}`}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.keys(PRIORITY_META).map((p) => (
                    <SelectItem key={p} value={p}>
                      <span className="inline-flex items-center gap-2">
                        <span className="h-2 w-2 rounded-full" style={{ background: PRIORITY_META[p].color }} />
                        {PRIORITY_META[p].label}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <button onClick={() => move(idx, -1)} disabled={idx === 0} className="text-zinc-400 hover:text-zinc-900 disabled:opacity-30 p-1"><ArrowUp className="h-4 w-4" /></button>
              <button onClick={() => move(idx, 1)} disabled={idx === items.length - 1} className="text-zinc-400 hover:text-zinc-900 disabled:opacity-30 p-1"><ArrowDown className="h-4 w-4" /></button>
              <button onClick={() => remove(idx)} className="text-zinc-400 hover:text-red-600 p-1" data-testid={`template-remove-${idx}`}><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
          <button onClick={add} className="flex items-center gap-2 px-4 py-3 text-sm text-zinc-500 hover:text-zinc-900 transition-colors w-full" data-testid="template-add">
            <Plus className="h-4 w-4" /> Add task
          </button>
        </div>
      )}

      <div className="mt-6 flex flex-wrap items-center justify-end gap-4">
        <label className="flex items-center gap-2 text-sm text-zinc-600 cursor-pointer" data-testid="template-sync-priority">
          <input type="checkbox" checked={syncPriority} onChange={(e) => setSyncPriority(e.target.checked)}
            className="h-4 w-4 rounded-sm border-zinc-300 accent-zinc-900" />
          Also update priorities on existing tasks
        </label>
        <Button onClick={save} disabled={saving} className="bg-zinc-900 hover:bg-zinc-800 rounded-sm" data-testid="template-save">
          {saving ? "Saving…" : "Save Template"}
        </Button>
      </div>
    </div>
  );
}
